import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
// TODO: wrap IconButton, MenuItem, Popover, Select, TextField as foundation primitives
import {
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  type TimeDisplayMode,
  useWorkspaceTimeBounds,
} from "@/shared/components/visualization/WorkspaceCtx";
import type { CanonicalDataType } from "@/shared/domain/topics";
import { usePopupState } from "@/shared/hooks/usePopupState";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import { buildColorMap } from "../search/highlightUtils";

import styles from "./ColumnFilterPopover.module.css";
import {
  conditionMeta,
  getConditionsForDataType,
  getFilterPlaceholder,
  isValuelessCondition,
  needsSecondValue,
  shouldUseDatetimePicker,
} from "./conditionMeta";
import { DatetimeFilterInput } from "./DatetimeFilterInput";
import {
  generateFilterStageColors,
  stageColorStyle,
} from "./filterStageColors";
import { isValidRegex } from "./regexUtils";
import {
  type ColumnFilter,
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
} from "./state";

export type ColumnFilterPopoverProps = {
  popup: ReturnType<typeof usePopupState<void>>;
  columnName: string;
  /** The column's messagePathId, used to look up highlight colors. */
  columnId?: string;
  /** The full filter state, used to compute a consistent color map. */
  filters?: Record<string, ColumnFilter>;
  stages: FilterStage[];
  dataType?: CanonicalDataType;
  timeDisplayMode?: TimeDisplayMode;
  combinator: StageCombinator;
  onStageChange: (stageIndex: number, stage: FilterStage) => void;
  onStageAdd: (stage: FilterStage) => void;
  onStageDelete: (stageIndex: number) => void;
  onCombinatorChange: (combinator: StageCombinator) => void;
};

/**
 * A popover used to apply one or more filter stages to a log panel column.
 *
 * Each stage has a condition dropdown and (for most conditions) a text input.
 * Multiple stages are combined with the shared combinator (AND / OR).
 *
 * The popover also contains an "Add condition" button to append new stages,
 * and a delete button on each stage when more than one stage exists.
 */
export function ColumnFilterPopover({
  popup,
  columnName,
  columnId,
  filters,
  stages,
  dataType,
  timeDisplayMode,
  combinator,
  onStageChange,
  onStageAdd,
  onStageDelete,
  onCombinatorChange,
}: ColumnFilterPopoverProps) {
  const availableConditions = getConditionsForDataType(dataType);
  const colorMap = React.useMemo(
    () => (filters ? buildColorMap(filters) : new Map<string, number>()),
    [filters],
  );
  const stageColors = React.useMemo(
    () => generateFilterStageColors(colorMap.size),
    [colorMap.size],
  );
  return (
    <Popover
      disableRestoreFocus
      disableAutoFocus
      open={popup.isOpen}
      anchorEl={popup.anchor}
      onClose={() => popup.close()}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          popup.close();
        }}
      >
        <div className={styles.treeRoot}>
          <span className={styles.columnHeader}>{columnName}:</span>
          <div className={styles.stageList}>
            {stages.map((stage, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className={styles.combinatorRow}>
                    <div className={styles.traceLine} />
                    <button
                      className={styles.combinatorToggle}
                      onClick={() =>
                        onCombinatorChange(combinator === "or" ? "and" : "or")
                      }
                      type="button"
                      aria-label={`Toggle filter combinator, currently ${combinator === "or" ? "OR" : "AND"}`}
                    >
                      {combinator === "or" ? "OR" : "AND"}
                    </button>
                  </div>
                )}
                <div
                  className={styles.stageEntry}
                  style={
                    columnId !== undefined &&
                    colorMap.has(`${columnId}:${index}`)
                      ? stageColorStyle(
                          colorMap.get(`${columnId}:${index}`)!,
                          stageColors,
                        )
                      : undefined
                  }
                >
                  <div className={styles.traceLine} />
                  <div className={styles.stageCard}>
                    <StageRow
                      stage={stage}
                      stageNumber={index + 1}
                      availableConditions={availableConditions}
                      autoFocusTextField={index === stages.length - 1}
                      showDelete={stages.length > 1}
                      dataType={dataType}
                      timeDisplayMode={timeDisplayMode}
                      onChange={(updated) => onStageChange(index, updated)}
                      onDelete={() => onStageDelete(index)}
                    />
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className={styles.traceEnd}>
              <div className={styles.traceLineDashed} />
              <IconButton
                size="small"
                title="Add condition"
                aria-label="Add another filter condition"
                onClick={() =>
                  onStageAdd({ value: "", condition: availableConditions[0] })
                }
                className={styles.addButton}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
      </form>
    </Popover>
  );
}

function StageRow({
  stage,
  stageNumber,
  availableConditions,
  autoFocusTextField,
  showDelete,
  dataType,
  timeDisplayMode,
  onChange,
  onDelete,
}: {
  stage: FilterStage;
  stageNumber: number;
  availableConditions: FilterCondition[];
  autoFocusTextField: boolean;
  showDelete: boolean;
  dataType?: CanonicalDataType;
  timeDisplayMode?: TimeDisplayMode;
  onChange: (stage: FilterStage) => void;
  onDelete: () => void;
}) {
  const condition = stage.condition;
  const isRegex = condition === "matches_regex";
  const isNumeric = conditionMeta[condition].numericOnly;
  const hasRegexError =
    isRegex && stage.value !== "" && !isValidRegex(stage.value);
  const hideTextField = isValuelessCondition(condition);
  const showSecondValue = needsSecondValue(condition);
  const useDatetimePicker = shouldUseDatetimePicker(
    dataType,
    timeDisplayMode,
    condition,
  );

  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });
  const earliestTime = timeBounds.earliest ?? null;

  return (
    <>
      <div className={styles.selectRow}>
        <Select
          size="small"
          fullWidth
          value={condition}
          aria-label={`Condition for stage ${stageNumber}`}
          onChange={(event) => {
            const nextCondition = event.target.value as FilterCondition;
            if (isValuelessCondition(nextCondition)) {
              onChange({ value: "", condition: nextCondition });
            } else {
              onChange({
                value: stage.value,
                condition: nextCondition,
                ...(needsSecondValue(nextCondition) &&
                  stage.value2 !== undefined && { value2: stage.value2 }),
              });
            }
          }}
        >
          {availableConditions.map((key) => (
            <MenuItem key={key} value={key}>
              {conditionMeta[key].label}
            </MenuItem>
          ))}
        </Select>
        {showDelete && (
          <IconButton
            size="small"
            className={styles.inlineDeleteButton}
            onClick={onDelete}
            title="Remove condition"
            aria-label="Delete filter stage"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {useDatetimePicker &&
        (() => {
          // shouldUseDatetimePicker guarantees timeDisplayMode is defined
          const mode = timeDisplayMode as TimeDisplayMode;
          return showSecondValue ? (
            <DatetimeFilterInput
              mode="between"
              layout="combined"
              from={stage.value}
              to={stage.value2 ?? ""}
              onChange={(from, to) =>
                onChange({ value: from, condition, value2: to })
              }
              timeDisplayMode={mode}
              earliestTime={earliestTime}
              textFieldProps={{ fullWidth: true, variant: "outlined" }}
              disablePopoverRestoreFocus
            />
          ) : (
            <DatetimeFilterInput
              mode="single"
              value={stage.value}
              onChange={(v) => onChange({ value: v, condition })}
              timeDisplayMode={mode}
              earliestTime={earliestTime}
              textFieldProps={{ fullWidth: true, variant: "outlined" }}
              disablePopoverRestoreFocus
            />
          );
        })()}
      {!useDatetimePicker && !hideTextField && (
        <TextField
          autoFocus={autoFocusTextField}
          fullWidth
          size="small"
          type={isNumeric ? "number" : "text"}
          placeholder={getFilterPlaceholder(isRegex, showSecondValue)}
          variant="outlined"
          value={stage.value}
          error={hasRegexError}
          onChange={(event) => {
            onChange({
              value: event.target.value,
              condition,
              ...(showSecondValue && { value2: stage.value2 ?? "" }),
            });
          }}
        />
      )}
      {!useDatetimePicker && showSecondValue && (
        <TextField
          fullWidth
          size="small"
          type="number"
          placeholder="Max"
          variant="outlined"
          value={stage.value2 ?? ""}
          onChange={(event) => {
            onChange({
              value: stage.value,
              condition,
              value2: event.target.value,
            });
          }}
        />
      )}
    </>
  );
}
